import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Flag, ChevronDown } from "lucide-react";
import {
  SECTIONS,
  CODING_ERROR_CATEGORIES,
  OASIS_ERROR_CATEGORIES,
} from "@/lib/oasisSections";

function isEmpty(val) {
  if (val == null) return true;
  if (typeof val === "string") return val.trim() === "";
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === "object") return Object.values(val).every((v) => isEmpty(v));
  return false;
}

function formatValue(item, v) {
  if (v == null) return "";
  if (item.type === "diagnosis_row") {
    const oe = v.oe === "O" ? "Onset" : v.oe === "E" ? "Exacerbation" : (v.oe || "");
    return [v.code, v.date, oe && `O/E: ${oe}`, v.severity && `Sev ${v.severity}`].filter(Boolean).join(" · ");
  }
  if (item.simple) return v.coder || "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") {
    const parts = [];
    if (!isEmpty(v.client)) parts.push(`Client: ${Array.isArray(v.client) ? v.client.join(", ") : v.client}`);
    if (!isEmpty(v.coder)) parts.push(`Coder: ${Array.isArray(v.coder) ? v.coder.join(", ") : v.coder}`);
    if (!isEmpty(v.rationale)) parts.push(`Rationale: ${v.rationale}`);
    return parts.join(" · ");
  }
  return String(v);
}

// Map an OASIS item id to the most-specific OASIS error category for the Flag pre-fill.
function defaultOasisCategoryForItem(item) {
  const id = item.id;
  if (id === "M1306" || id === "M1311" || id === "M1322" || id === "M1324"
      || id === "M1330" || id === "M1332" || id === "M1334"
      || id === "M1340" || id === "M1342") return "Wound Assessment Errors";
  if (id === "M2010" || id === "M2020" || id === "M2030" || id === "N0415") return "Medication Assessment Errors";
  if (id.startsWith("M18") || id.startsWith("GG0130") || id.startsWith("GG0170")) return "Functional Assessment Errors";
  if (id.startsWith("J0510") || id.startsWith("J0520") || id.startsWith("J0530")
      || id === "K0520" || id === "O0110"
      || id === "M1400" || id === "M1410" || id === "M1600"
      || id === "M1610" || id === "M1620") return "Clinical Assessment Errors";
  if (id.startsWith("M10") || id === "M1028" || id === "M1033") return "M-Item Errors";
  return "M-Item Errors";
}

function OasisAuditViewer({ oasisData, onFlagItem }) {
  const [openKey, setOpenKey] = useState(null);
  const filledSections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((it) => !isEmpty(oasisData?.[it.id])),
  })).filter((s) => s.items.length > 0);

  if (filledSections.length === 0) {
    return <div className="text-sm text-slate-400 italic py-2">No OASIS assessment data was submitted with this chart.</div>;
  }

  const totalItems = filledSections.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div className="space-y-2" data-testid="oasis-audit-viewer">
      <div className="text-xs text-slate-500 mb-1">{totalItems} item{totalItems === 1 ? "" : "s"} captured by coder · click <Flag size={11} className="inline -mt-0.5"/> to flag an OASIS error</div>
      {filledSections.map((s) => {
        const open = openKey === s.key;
        return (
          <div key={s.key} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`oasis-audit-section-${s.key}`}>
            <button type="button" onClick={() => setOpenKey(open ? null : s.key)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left">
              <span className="text-sm font-medium text-slate-800">{s.title}</span>
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                {s.items.length} item{s.items.length === 1 ? "" : "s"}
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
              </span>
            </button>
            {open && (
              <div className="divide-y divide-slate-100">
                {s.items.map((it) => (
                  <div key={it.id} className="px-3 py-2 grid grid-cols-[1fr_auto] gap-2 items-start">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 mb-0.5">{it.label}</div>
                      <div className="text-sm text-slate-800 break-words" data-testid={`oasis-audit-${it.id}-value`}>
                        {formatValue(it, oasisData[it.id]) || <span className="text-slate-400">—</span>}
                      </div>
                    </div>
                    <button type="button" onClick={() => onFlagItem(it)}
                            data-testid={`oasis-audit-flag-${it.id}`}
                            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 rounded px-2 py-1">
                      <Flag size={11}/> Flag
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ErrorList({ section, errors, setErrors, categories }) {
  const addError = () => setErrors([...errors, { category: categories[0], description: "", comment: "" }]);
  const updateError = (i, patch) => setErrors(errors.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const removeError = (i) => setErrors(errors.filter((_, idx) => idx !== i));

  return (
    <div data-testid={`${section}-error-list`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-700">{section === "coding" ? "Coding Errors" : "OASIS Errors"}</label>
        <button type="button" onClick={addError} data-testid={`${section}-add-error-btn`}
                className="inline-flex items-center gap-1 text-sm text-sky-700 hover:text-sky-900">
          <Plus size={14}/> Add {section === "coding" ? "coding" : "OASIS"} error
        </button>
      </div>
      <div className="space-y-2">
        {errors.length === 0 && <div className="text-sm text-slate-400 italic">No {section} errors — clean for this section.</div>}
        {errors.map((er, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`${section}-error-row-${i}`}>
            <div className="grid sm:grid-cols-[260px_1fr_auto] gap-2">
              <select value={er.category} onChange={(e) => updateError(i, { category: e.target.value })}
                      data-testid={`${section}-error-category-${i}`}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={er.description} onChange={(e) => updateError(i, { description: e.target.value })}
                     data-testid={`${section}-error-desc-${i}`}
                     placeholder="What went wrong?"
                     className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
              <button type="button" onClick={() => removeError(i)} className="text-slate-400 hover:text-red-600 px-2" data-testid={`${section}-error-remove-${i}`}>
                <X size={16}/>
              </button>
            </div>
            <textarea value={er.comment || ""} onChange={(e) => updateError(i, { comment: e.target.value })}
                      data-testid={`${section}-error-comment-${i}`}
                      rows={2}
                      placeholder="Add a comment for the coder (correct code, source guidance, OASIS-E reference)…"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuditForm() {
  const { chartId } = useParams();
  const nav = useNavigate();
  const [chart, setChart] = useState(null);
  const [oasisScore, setOasisScore] = useState(95);
  const [codingScore, setCodingScore] = useState(95);
  const [codingErrors, setCodingErrors] = useState([]);
  const [oasisErrors, setOasisErrors] = useState([]);
  const [codingComments, setCodingComments] = useState("");
  const [oasisComments, setOasisComments] = useState("");
  const [correctCodes, setCorrectCodes] = useState("");
  const [correctOasisResponses, setCorrectOasisResponses] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currentAction, setCurrentAction] = useState("");

  useEffect(() => {
    api.get(`/charts/${chartId}`).then((r) => {
      setChart(r.data);
      const a = r.data.audit;
      if (a) {
        if (a.oasis_score != null) setOasisScore(a.oasis_score);
        if (a.coding_score != null) setCodingScore(a.coding_score);
        // Prefer the new split fields, fall back to splitting the legacy errors[] array
        if (Array.isArray(a.coding_errors) || Array.isArray(a.oasis_errors)) {
          setCodingErrors(a.coding_errors || []);
          setOasisErrors(a.oasis_errors || []);
        } else {
          const errs = a.errors || [];
          const isOasis = a.audit_type === "oasis";
          setCodingErrors(errs.filter((e) => e.section === "coding" || (!e.section && !isOasis)));
          setOasisErrors(errs.filter((e) => e.section === "oasis" || (!e.section && isOasis)));
        }
        setCodingComments(a.coding_comments || (a.audit_type === "coding" ? (a.comments || "") : ""));
        setOasisComments(a.oasis_comments || (a.audit_type === "oasis" ? (a.comments || "") : ""));
        setCorrectCodes(a.correct_codes || "");
        setCorrectOasisResponses(a.correct_oasis_responses || "");
      }
    });
  }, [chartId]);

  const overallScore = useMemo(() => {
    const c = Number(codingScore);
    const o = Number(oasisScore);
    if (Number.isFinite(c) && Number.isFinite(o)) return Math.round(((c + o) / 2) * 100) / 100;
    return 0;
  }, [codingScore, oasisScore]);

  const flagOasisItem = (item) => {
    const codedValue = formatValue(item, chart?.oasis_data?.[item.id]);
    setOasisErrors((prev) => [...prev, {
      category: defaultOasisCategoryForItem(item),
      description: `${item.id} — coder entered: ${codedValue || "(empty)"}`,
      comment: "",
    }]);
    toast.success(`Flagged ${item.id} — review the new OASIS error row below`);
  };

  const runAction = async (action) => {
    // Validation — both score fields required for non-draft submissions
    const isDraft = action === "draft";
    if (!isDraft) {
      if (codingScore === "" || codingScore == null || oasisScore === "" || oasisScore == null) {
        toast.error("Both Coding Score (%) and OASIS Score (%) are required to submit");
        return;
      }
    }
    setSubmitting(true);
    setCurrentAction(action);
    try {
      await api.post(`/charts/${chartId}/audit`, {
        score: overallScore,
        oasis_score: Number(oasisScore),
        coding_score: Number(codingScore),
        audit_type: "combined",
        coding_errors: codingErrors.filter((er) => er.description.trim()).map((e) => ({ ...e, section: "coding" })),
        oasis_errors: oasisErrors.filter((er) => er.description.trim()).map((e) => ({ ...e, section: "oasis" })),
        coding_comments: codingComments,
        oasis_comments: oasisComments,
        comments: [codingComments, oasisComments].filter(Boolean).join("\n\n"),
        correct_codes: correctCodes,
        correct_oasis_responses: correctOasisResponses,
        save_as_draft: isDraft,
        action,
      });
      const msgs = { draft: "Saved as draft", submit: "Audit submitted", rework: "Rework requested", send_back: "Sent back to coder", re_audit: "Re-audit recorded" };
      toast.success(msgs[action] || "Saved");
      if (action !== "draft") nav("/qa/assigned");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
      setCurrentAction("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await runAction("submit");
  };

  if (!chart) return <div className="p-10 text-slate-500">Loading chart…</div>;

  const hasOasisData = chart.oasis_data && Object.keys(chart.oasis_data).length > 0;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="audit-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={14}/> Back
      </button>

      <PageHeader
        title={`Audit · ${chart.chart_id}`}
        subtitle={`Patient ${chart.patient_name} · ${chart.assessment_type} · ${chart.assessment_date} · Coder ${chart.submitted_by_name}`}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart summary */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5 h-fit">
          <h3 className="font-heading text-lg font-medium mb-3">Submitted codes</h3>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">M1021 — Primary Diagnosis</div>
          <div className="mb-4">
            {chart.m1021_primary ? (
              <span className="bg-sky-50 text-sky-700 text-sm font-mono rounded px-2 py-0.5">{chart.m1021_primary}</span>
            ) : <span className="text-slate-400 text-sm">—</span>}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">M1023 — Other Diagnoses</div>
          <div className="space-y-1 mb-4">
            {(chart.m1023_codes || []).length ? chart.m1023_codes.map((row) => (
              <div key={row.slot} className="text-sm font-mono">
                <span className="text-slate-500 mr-2">{row.slot}</span>
                <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{row.code}</span>
              </div>
            )) : <span className="text-slate-400 text-sm">—</span>}
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{chart.notes || "—"}</p>

          {hasOasisData && (
            <div className="mt-5 pt-5 border-t border-slate-200">
              <h3 className="font-heading text-base font-medium mb-2">OASIS Assessment</h3>
              <OasisAuditViewer oasisData={chart.oasis_data} onFlagItem={flagOasisItem} />
            </div>
          )}
        </div>

        {/* Audit form */}
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 space-y-6" data-testid="audit-form">
          <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900 flex items-center gap-2" data-testid="audit-type-banner">
            <span className="inline-flex items-center justify-center bg-sky-700 text-white text-[10px] font-semibold rounded px-1.5 py-0.5">CODING + OASIS</span>
            Both Coding and OASIS are audited together on this chart. Complete both sections to submit.
          </div>

          {/* Scores */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Coding Accuracy (%)</label>
              <input data-testid="coding-score-input" type="number" min={0} max={100} step={0.5}
                     value={codingScore} onChange={(e) => setCodingScore(e.target.value)} required
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
              <div className="text-xs text-slate-500 mt-1">Independent score for ICD-10 / diagnosis coding.</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">OASIS Accuracy (%)</label>
              <input data-testid="oasis-score-input" type="number" min={0} max={100} step={0.5}
                     value={oasisScore} onChange={(e) => setOasisScore(e.target.value)} required
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
              <div className="text-xs text-slate-500 mt-1">Independent score for OASIS-E items.</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Overall Audit Score (%)</label>
              <input data-testid="score-input" type="number" value={overallScore} readOnly
                     className="w-full border border-slate-300 bg-slate-50 rounded-lg px-3 py-2 text-sm font-semibold text-slate-800"/>
              <div className="text-xs text-slate-500 mt-1">Auto = (Coding + OASIS) / 2.</div>
            </div>
          </div>

          {/* Coding section */}
          <div className="border-t border-slate-200 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center bg-emerald-100 text-emerald-800 text-[11px] font-semibold rounded px-2 py-0.5 uppercase tracking-wider">Section · Coding</span>
            </div>
            <ErrorList section="coding" errors={codingErrors} setErrors={setCodingErrors} categories={CODING_ERROR_CATEGORIES} />
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Coding Comments</label>
              <textarea data-testid="coding-comments-input" value={codingComments} onChange={(e) => setCodingComments(e.target.value)} rows={3}
                        placeholder="Section-level comments on coding accuracy, guideline notes, etc…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Correct ICD-10 Codes (coder guidance)</label>
              <textarea data-testid="correct-codes-input" rows={2} value={correctCodes} onChange={(e) => setCorrectCodes(e.target.value)}
                        placeholder="e.g. M1021: I11.9; M1023A: I50.9 — explain why and reference OCG / convention…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
          </div>

          {/* OASIS section */}
          <div className="border-t border-slate-200 pt-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center bg-indigo-100 text-indigo-800 text-[11px] font-semibold rounded px-2 py-0.5 uppercase tracking-wider">Section · OASIS</span>
            </div>
            <ErrorList section="oasis" errors={oasisErrors} setErrors={setOasisErrors} categories={OASIS_ERROR_CATEGORIES} />
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">OASIS Comments</label>
              <textarea data-testid="oasis-comments-input" value={oasisComments} onChange={(e) => setOasisComments(e.target.value)} rows={3}
                        placeholder="Section-level comments on OASIS responses, skip patterns, item conventions…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Correct OASIS Responses (coder guidance)</label>
              <textarea data-testid="correct-oasis-input" rows={2} value={correctOasisResponses} onChange={(e) => setCorrectOasisResponses(e.target.value)}
                        placeholder="e.g. M1860: 3 not 2 — patient requires two-handed device per documentation…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" disabled={submitting} onClick={() => runAction("draft")}
                    data-testid="save-draft-btn"
                    className="bg-white border border-slate-300 text-slate-800 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-60">
              {currentAction === "draft" ? "Saving…" : "Save as Draft"}
            </button>
            <button type="button" disabled={submitting} onClick={() => runAction("rework")}
                    data-testid="request-rework-btn"
                    className="bg-amber-100 border border-amber-300 text-amber-900 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-amber-200 disabled:opacity-60">
              {currentAction === "rework" ? "Saving…" : "Request Rework"}
            </button>
            <button type="button" disabled={submitting} onClick={() => runAction("send_back")}
                    data-testid="send-back-btn"
                    className="bg-rose-100 border border-rose-300 text-rose-900 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-rose-200 disabled:opacity-60">
              {currentAction === "send_back" ? "Saving…" : "Send Back to Coder"}
            </button>
            <button data-testid="submit-audit-btn" disabled={submitting}
                    className="bg-sky-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
              {currentAction === "submit" || (submitting && !currentAction) ? "Submitting…" : (chart.audit ? "Update audit" : "Submit audit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
